import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useState,
} from "react";
import { type UseTimerReturn, useTimer } from "@/v2/hooks/useTimer";

interface TimerContextType extends UseTimerReturn {
	/** Duration (seconds) pending entry creation — set automatically when timer stops */
	pendingDuration: number | null;
	/** Open the add-entry flow with the stopped timer's duration (for manual re-trigger) */
	requestAddEntry: () => void;
	/** Permanently finish the timer, collapse it, and open the add-entry flow. */
	finishAndRequestAddEntry: () => void;
	/** Clear pendingDuration without dismissing the timer */
	clearPendingDuration: () => void;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export function TimerProvider({ children }: { children: ReactNode }) {
	const parentContext = useContext(TimerContext);
	if (parentContext) return <>{children}</>;

	return <TimerProviderRoot>{children}</TimerProviderRoot>;
}

function TimerProviderRoot({ children }: { children: ReactNode }) {
	const [pendingDuration, setPendingDuration] = useState<number | null>(null);

	const timer = useTimer({
		onStop: (elapsed) => setPendingDuration(elapsed),
	});

	const requestAddEntry = useCallback(() => {
		if (timer.phase === "stopped" && timer.finalSeconds > 0) {
			setPendingDuration(timer.finalSeconds);
		}
	}, [timer.phase, timer.finalSeconds]);

	const finishAndRequestAddEntry = useCallback(() => {
		timer.finish();
	}, [timer]);

	const clearPendingDuration = useCallback(() => {
		setPendingDuration(null);
	}, []);

	return (
		<TimerContext.Provider
			value={{
				...timer,
				pendingDuration,
				requestAddEntry,
				finishAndRequestAddEntry,
				clearPendingDuration,
			}}
		>
			{children}
		</TimerContext.Provider>
	);
}

export function useTimerContext() {
	const context = useContext(TimerContext);
	if (!context)
		throw new Error("useTimerContext must be used within TimerProvider");
	return context;
}
